import { useCallback, useEffect, useRef, useState } from "react";
import { Eye, FileWarning, Lock, PenLine } from "lucide-react";
import type { PublicQuote } from "@forgepoint/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { mergeAssetUrls, type AssetUrlOverrides } from "./assetUrlOverrides";
import { LockedDocumentView } from "./LockedDocumentView";
import { QuoteDocumentEditor } from "./QuoteDocumentEditor";
import { QuoteDocumentPreview } from "./QuoteDocumentPreview";
import { QuoteDocumentSkeleton } from "./QuoteDocumentSkeleton";
import type { DocumentAssetUrls } from "./types";
import {
  useQuoteDocumentsStore,
  type StoredQuoteDocument,
} from "./quoteDocumentsStore";

/** Shown while the quote is locked, in place of the editable-document hint. */
const READ_ONLY_NOTICE =
  "Read-only preview of the last-saved quote. Save changes on the Quote tab to see them here.";

interface QuoteDocumentTabProps {
  /** The saved quote's id, backing the document GET/PUT. */
  quoteId: string;
  /** The customer-safe quote projection bound into the canvas. */
  publicQuote: PublicQuote;
  /** Resolved signed asset urls keyed by asset id. */
  assetUrls: DocumentAssetUrls;
  /**
   * When true the tab renders the document as a view-only preview: no toolbar,
   * palette, properties panel, source drawer, or Save button, and no editing
   * chrome is mounted. Mirrors the quote fields' read-only lock (finalized,
   * archived, or no edit permission).
   */
  readOnly?: boolean;
  /** Whether the org permits editing individual quote documents. */
  allowEditing: boolean;
  /** Called after an autosave persists, with the saved document HTML. */
  onDocumentSaved?: (html: string) => void;
}

/**
 * The quote editor's Document tab: a preview of the quote's stored HTML
 * document with an "Edit document" entry point. The sheet is too narrow for
 * the three-pane WYSIWYG editor, so editing opens QuoteDocumentEditor in a
 * full-screen dialog; the tab itself always renders the last-saved document
 * through the production QuoteDocumentRenderer/PdfPageViewer stack.
 *
 * The document seeds from the backend on open (per-entity GET through the
 * quote-documents store) and persists through debounced autosave under the
 * document's optimistic-concurrency version, with explicit 409-conflict
 * recovery and a save-state indicator in the editor header. Switching quotes
 * clears the seed and closes the editor until the new quote's document arrives.
 *
 * The canvas binds the server-computed publicQuote projection, so it reflects
 * the last-saved quote data - edits made in the Quote tab are not re-projected
 * here until the quote is saved and reloaded.
 *
 * Images uploaded inside the editor are held here as local blob overrides so
 * their preview survives the editor closing on save, before the quote refetch
 * brings back a signed url for the new asset.
 * @param quoteId - The saved quote's id
 * @param publicQuote - The customer-safe quote projection
 * @param assetUrls - Resolved signed asset urls keyed by asset id
 * @param readOnly - When true, render a view-only preview with no editing chrome
 * @param allowEditing - Whether the org permits editing individual quote documents
 * @param onDocumentSaved - Called after an autosave persists, with the saved HTML
 */
export function QuoteDocumentTab({
  quoteId,
  publicQuote,
  assetUrls,
  readOnly = false,
  allowEditing,
  onDocumentSaved,
}: QuoteDocumentTabProps) {
  const fetchDocument = useQuoteDocumentsStore((state) => state.fetchDocument);
  const state = useQuoteDocumentsStore((store) =>
    store.documentStates.get(quoteId),
  );
  const storedDocument = useQuoteDocumentsStore((store) =>
    store.documents.get(quoteId),
  );
  const [seed, setSeed] = useState<StoredQuoteDocument | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const discardedRef = useRef(false);
  const [assetOverrides, setAssetOverrides] = useState<AssetUrlOverrides>({});
  const overrideUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    let active = true;
    setSeed(null);
    setEditorOpen(false);
    fetchDocument(quoteId).then((document) => {
      if (active && document) {
        setSeed((current) =>
          current === null || document.version >= current.version
            ? document
            : current,
        );
      }
    });
    return () => {
      active = false;
    };
  }, [fetchDocument, quoteId]);

  useEffect(() => {
    if (storedDocument && !editorOpen) {
      setSeed((current) =>
        current === null || storedDocument.version > current.version
          ? storedDocument
          : current,
      );
    }
  }, [storedDocument, editorOpen]);

  useEffect(
    () => () => {
      overrideUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  const registerLocalAsset = useCallback((assetId: string, file: File) => {
    const objectUrl = URL.createObjectURL(file);
    overrideUrlsRef.current = [...overrideUrlsRef.current, objectUrl];
    setAssetOverrides((current) => ({ ...current, [assetId]: objectUrl }));
  }, []);

  const mergedAssetUrls = mergeAssetUrls(assetUrls, assetOverrides);

  if (!seed) {
    return (
      <div className="h-full overflow-auto bg-muted">
        {state?.getError ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <FileWarning className="size-8 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">
                The document could not be loaded
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {state.getError}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                fetchDocument(quoteId).then(
                  (document) => document && setSeed(document),
                )
              }
            >
              Try again
            </Button>
          </div>
        ) : (
          <QuoteDocumentSkeleton />
        )}
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex shrink-0 items-center justify-between gap-3 border-b bg-background px-4 py-2">
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="size-3" aria-hidden />
            {READ_ONLY_NOTICE}
          </p>
          <Button size="sm" onClick={() => setPreviewOpen(true)}>
            <Eye className="size-4" /> Preview document
          </Button>
        </div>
        <QuoteDocumentPreview
          publicQuote={publicQuote}
          assetUrls={mergedAssetUrls}
          documentHtml={seed.document}
        />
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent
            aria-describedby={undefined}
            className="inset-0 flex h-full w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0 sm:rounded-none [&>button]:hidden"
          >
            <DialogTitle className="sr-only">Preview document</DialogTitle>
            <LockedDocumentView
              publicQuote={publicQuote}
              assetUrls={mergedAssetUrls}
              documentHtml={seed.document}
              notice={READ_ONLY_NOTICE}
              onClose={() => setPreviewOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const closeEditor = () => {
    if (editorDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    setEditorOpen(false);
  };

  const discardAndClose = () => {
    discardedRef.current = true;
    setConfirmDiscardOpen(false);
    setEditorDirty(false);
    setEditorOpen(false);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b bg-background px-4 py-2">
        <p className="text-xs text-muted-foreground">
          Preview of the last-saved document. Quote tab changes appear after the
          quote is saved.
        </p>
        {allowEditing && (
          <Button
            size="sm"
            onClick={() => {
              discardedRef.current = false;
              setEditorOpen(true);
            }}
          >
            <PenLine className="size-4" /> Edit document
          </Button>
        )}
      </div>
      <QuoteDocumentPreview
        publicQuote={publicQuote}
        assetUrls={mergedAssetUrls}
        documentHtml={seed.document}
      />
      <Dialog open={editorOpen} onOpenChange={(open) => !open && closeEditor()}>
        <DialogContent
          aria-describedby={undefined}
          onEscapeKeyDown={(event) => event.preventDefault()}
          className="inset-0 flex h-full w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0 sm:rounded-none [&>button]:hidden"
        >
          <DialogTitle className="sr-only">Edit document</DialogTitle>
          <QuoteDocumentEditor
            key={quoteId}
            quoteId={quoteId}
            publicQuote={publicQuote}
            assetUrls={mergedAssetUrls}
            seed={seed}
            onLocalAsset={registerLocalAsset}
            discardedRef={discardedRef}
            onSaved={(document) =>
              setSeed((current) =>
                current === null || document.version >= current.version
                  ? document
                  : current,
              )
            }
            onDocumentSaved={onDocumentSaved}
            onDirtyChange={setEditorDirty}
            onRequestClose={closeEditor}
          />
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={confirmDiscardOpen}
        onOpenChange={setConfirmDiscardOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              The document has unsaved changes. Closing the editor will discard
              them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={discardAndClose}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
